/**
 * Orders
 */

// React
import React, {FunctionComponent, useEffect, useState, useContext} from 'react';
import {useForm} from 'react-hook-form';

// Context
import AuthContext, {AuthConsumer} from '../../authContext';

// Redux
import {connect} from 'react-redux';
import {compose} from 'redux';
import {
  getOrdersAction,
  updateOrderStatusActions,
  updateAllOrderStatusActions,
  actionRetrieveRubikaOrdersAction,
  cancelOrderAction,
  getAllOrdersReportAction,
} from './actions';
import {Store} from '../../redux/reducers';

// Selector
import {createStructuredSelector} from 'reselect';
import {
  makeSelectOrdersList,
  makeSelectLoading,
  makeSelectError,
  makeSelectCountList,
  makeSelectAllOrders,
} from './selectors';

// Router
import {useParams} from 'react-router-dom';

// UI
import clsx from 'clsx';
import theme from '../../theme';
import {makeStyles, createStyles, Theme} from '@material-ui/core/styles';
import {
  CardHeader,
  CardContent,
  Card,
  Typography,
  Grid,
  CircularProgress,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  InputBase,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  ClickAwayListener,
  Paper,
} from '@material-ui/core';

// Icon
import Icon from '@mdi/react';
import {
  mdiAccountKeyOutline,
  mdiAccountOutline,
  mdiPhoneOutline,
  mdiHomeSearchOutline,
  mdiPackageVariant,
  mdiStateMachine,
  mdiCalendar,
  mdiMagnify,
  mdiClose,
  mdiHomeCityOutline,
  mdiPencilOutline,
} from '@mdi/js';

// Components
import RenderFilter from '../../utils/listDaynamicFilter';
import type {Filter} from '../../utils/listDaynamicFilter';
import List from './list';
import Pagination from '@material-ui/lab/Pagination';
import Can from '../../components/Can';
import CSV from './csv';

// Helper
import {
  removeEmptyValueObject,
  translate,
  checkMatchMedia,
} from '../../utils/helpers';

// Type
import {
  Props,
  SelectorProps,
  Payload,
  Status,
  cancelPayload,
  PayloadAllUpdate,
} from './interfaces';

// Style
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {},
    paper: {},
    loading: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      height: '50vh',
      alignSelf: 'center',
      justifyContent: 'center',
      background: '#ccc',
    },
    pagination: {
      // marginTop: theme.spacing(1),
    },
    searchBox: {
      marginRight: '140px',
      display: 'flex',
      position: 'relative',
      backgroundColor: theme.palette.common.white,
      borderRadius: '4px',
      // boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.1)',
      border: 'solid 1px #8a8a8a',
    },
    input: {
      display: 'flex',
      marginLeft: theme.spacing(1),
      flex: 2,
      '@media (max-width: 768px)': {
        fontSize: '12px',
      },
    },
    iconButton: {
      padding: 8,
      position: 'relative',
      '&:before': {
        content: '""',
        position: 'absolute',
        width: '1px',
        height: '36px',
        top: '4px',
        left: '-9px',
        backgroundColor: '#EDF1F4',
      },
    },
    textArea: {
      width: '100%',
    },
    filterBox: {
      position: 'absolute',
      backgroundColor: '#fff',
      width: '100%',
      marginTop: '12px',
      top: '47px',
      right: 0,
      borderRadius: '0  0 4px  4px',
      padding: '16px 16px',
      boxSizing: 'border-box',
      boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.1)',
      zIndex: 10,
    },
    filterOpen: {
      display: 'block',
    },
    filterClose: {
      display: 'none',
    },
    paginationBox: {
      display: 'flex',
    },
    selectSize: {
      width: '35px',
    },
    subtitle: {
      fontSize: '14px',
      fontWeight: 500,
      color: '#8A8A8A',
    },
    magnifyIcon: {
      transform: 'rotate(-90deg)',
    },
    // Cancel Dialog
    cancelIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),

      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.error.main,
      },
    },
    cancelTitle: {
      fontSize: '16px',
      fontWeight: 500,
      textAlign: 'justify',
      lineHeight: 2,
      marginBottom: theme.spacing(3),
    },
    actionsBox: {
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
    },
    editIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.blue.main,
      },
    },
    btnBox: {
      marginTop: '14px',
      display: 'flex',
      flexDirection: 'row-reverse',
      width: '100%',
      '@media (max-width: 768px)': {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
      },
    },
    typographyCards: {
      fontSize: '16px',
      color: 'white',
      backgroundColor: '#4BCF90',
      padding: '8px 24px 8px 50px',
      marginLeft: '-40px',
    },
    card: {
      width: '100%',
      marginBottom: '14px',
      overflow: 'visible',
      padding: '14px 0 0',
      paddingBottom: '0',
    },

    cardContent: {
      justifyContent: 'space-between',
      padding: ' 0',
    },
    cardContentOrder: {
      padding: '0',
    },
    headerBox: {
      padding: '5px',

      marginLeft: '75px',
      display: 'flex',
      alignItems: 'center',
      marginBottom: '10px',
      marginTop: '10px',
    },
  })
);

type Inputs = {
  'customerNationalCode.contains': string;
};

const VariantOrders: FunctionComponent<Props> = (props: Props) => {
  const authContext = useContext(AuthContext);
  const {register, handleSubmit, reset} = useForm<Inputs>();
  const [filters, setFilters] = useState([] as Filter[]);
  const [filtersValues, setFiltersValues] = useState({
    'itemsSkuId.equals': props.match?.params?.id,
    page: 0,
    size: 10,
  } as any);
  const [openSize, setOpenSize] = React.useState(false as boolean);
  const [openConfirmCancel, setOpenConfirmCancel] = useState(false as boolean);
  const [openConfirmUpdateState, setOpenConfirmUpdateState] = useState(
    false as boolean
  );

  const [id, setId] = useState(-1 as number);
  const [previewState, setPreviewState] = useState('CANCELLED' as Status);
  const [cancelDescription, setCancelDescription] = useState('' as string);
  const [lastUpdate, setLastUpdate] = useState(
    '2019-07-02T17:15:03+04:30' as string
  );
  const [openFilterBox, setOpenFilterBox] = React.useState(false);

  const urlParams = useParams();

  const handleClick = () => {
    setOpenFilterBox(prev => !prev);
  };

  const onSubmit = (filtervalues: any) => {
    setFiltersValues({
      ...removeEmptyValueObject(filtervalues),
      'itemsSkuId.equals': props.match?.params?.id,
      page: 0,
      size: 10,
    });
    reset({});
    setOpenFilterBox(false);
  };

  // Bad Bad Bad.. :(
  props.ordersList[0]?.created &&
    props.ordersList[0]?.created > lastUpdate &&
    setLastUpdate(props.ordersList[0]?.created);

  useEffect(() => {
    getOrders();
    setupFilters();
  }, [filtersValues]); // eslint-disable-line react-hooks/exhaustive-deps

  const classes = useStyles();
  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setFiltersValues({...filtersValues, page: page - 1});
  };

  const getOrders = () => {
    const {page, size} = filtersValues;
    props.ordersDispatch({
      ...filtersValues,
      page,
      size,
    });
  };

  const setupFilters = () => {
    Can({
      role: authContext.user.role,
      perform: 'orders:filtersByCenterId',
      yes: () => {
        setFilters([
          {
            type: 'input',
            id: 'customerName.contains',
            label: 'نام سفارش‌دهنده',
            icon: mdiAccountOutline,
          },
          {
            type: 'input',
            id: 'customerPhoneNumber.contains',
            label: 'شماره همراه سفارش‌دهنده',
            icon: mdiPhoneOutline,
          },
          {
            type: 'input',
            id: 'id.equals',
            label: 'شناسه سفارش',
            icon: mdiPackageVariant,
          },

          {
            type: 'input',
            id: 'trackingCode.contains',
            label: 'کد پیگیری',
            icon: mdiPackageVariant,
          },
          {
            type: 'input',
            id: 'customerNationalCode.contains',
            label: 'کد  ملی',
            icon: mdiPackageVariant,
          },

          {
            type: 'input',
            id: 'centerName.contains',
            label: 'نام مرکز',
            icon: mdiHomeSearchOutline,
          },

          {
            type: 'input',
            id: 'centerProvince.contains',
            label: 'نام  استان',
            icon: mdiHomeCityOutline,
          },
          {
            type: 'input',
            id: 'centerCity.contains',
            label: 'نام  شهر',
            icon: mdiHomeCityOutline,
          },

          {
            type: 'date-picker-time',
            id: 'created.greaterThanOrEqual',
            label: 'تاریخ ثبت سفارش از ',
            icon: mdiCalendar,
          },
          {
            type: 'date-picker-time',
            id: 'created.lessThanOrEqual',
            label: 'تا تاریخ ثبت سفارش  ',
            icon: mdiCalendar,
          },
          {
            type: 'date-picker',
            id: 'deliveryDate.greaterThanOrEqual',
            label: 'تاریخ تحویل از ',
            icon: mdiCalendar,
          },
          {
            type: 'date-picker',
            id: 'deliveryDate.lessThanOrEqual',
            label: '  تا تاریخ تحویل ',
            icon: mdiCalendar,
          },
          {
            type: 'checkbox',
            id: 'status.in',
            label: 'وضعیت سفارش',
            icon: mdiStateMachine,
            data: [
              'ORDERED',
              'DELIVERED_TO_POST',
              'DELIVERED_TO_CENTER',
              'SUPPLIED_IN_CENTER',
              'PACKAGED',
              'BUNDLED',
              'LOADED',
              'ARRIVED',
              'DELIVERED',
              'NOT_DELIVERED',
              'RETURNED',
              'CANCELLED',
            ],
            translateType: 'Order',
          },
        ]);
      },
      no: () => {
        setFilters([
          {
            type: 'input',
            id: 'customerName.contains',
            label: 'نام سفارش‌دهنده',
            icon: mdiAccountOutline,
          },
          {
            type: 'input',
            id: 'customerPhoneNumber.contains',
            label: 'شماره همراه سفارش‌دهنده',
            icon: mdiPhoneOutline,
          },
          {
            type: 'input',
            id: 'id.equals',
            label: 'شناسه سفارش',
            icon: mdiPackageVariant,
          },

          // {
          //   type: 'input',
          //   id: 'trackingCode.contains',
          //   label: 'کد پیگیری',
          // },

          {
            type: 'input',
            id: 'customerNationalCode.contains',
            label: 'کد  ملی',
            icon: mdiAccountKeyOutline,
          },
          // {
          //   type: 'input',
          //   id: 'centerProvince.contains',
          //   label: 'نام  استان',
          // },
          // {
          //   type: 'input',
          //   id: 'centerCity.contains',
          //   label: 'نام  شهر',
          // },

          // {
          //   type: 'date-picker-time',
          //   id: 'created.greaterThanOrEqual',
          //   label: 'تاریخ ثبت سفارش از ',

          // }, {
          //   type: 'date-picker-time',
          //   id: 'created.lessThanOrEqual',
          //   label: 'تا تاریخ ثبت سفارش  ',

          // },
          {
            type: 'date-picker',
            id: 'deliveryDate.greaterThanOrEqual',
            label: 'تاریخ تحویل از ',
            icon: mdiCalendar,
          },
          {
            type: 'date-picker',
            id: 'deliveryDate.lessThanOrEqual',
            label: '  تا تاریخ تحویل ',
            icon: mdiCalendar,
          },
          {
            type: 'checkbox',
            id: 'status.in',
            label: 'وضعیت سفارش',
            icon: mdiStateMachine,
            data: [
              'ORDERED',
              'DELIVERED_TO_POST',
              'DELIVERED_TO_CENTER',
              'SUPPLIED_IN_CENTER',
              'PACKAGED',
              'BUNDLED',
              'LOADED',
              'ARRIVED',
              'DELIVERED',
              'NOT_DELIVERED',
              'RETURNED',
              'CANCELLED',
            ],
            translateType: 'Order',
          },
        ]);
      },
    });
  };

  const handleCancelOrder = async () => {
    await props.cancelOrderDispatch({
      id,
      body: {description: cancelDescription},
    });
    getOrders();
    setOpenConfirmCancel(false);
  };

  const changeStatusOrder = () => {
    props.updateOrderStatusDispatch({
      id,
      body: previewState,
      pagination: filtersValues,
    });
    setOpenConfirmUpdateState(false);
  };
  const changeStatusOrderWhitId = (data: any) => {
    const {ids, status, description} = data;
    props.updateAllOrderStatusDispatch({
      ids,
      status,
      description,
      pagination: filtersValues,
    });

    // props.updateOrderStatusDispatch({
    //   id,
    //   body,
    //   pagination: filtersValues,
    // });
    setOpenConfirmUpdateState(false);
  };

  const handleOpenConfirmCancele = (id: number) => {
    setOpenConfirmCancel(true);
    setId(id);
  };
  const handleOpenConfirmUpdateState = (data: {id: number; body: Status}) => {
    setOpenConfirmUpdateState(true);
    setId(data.id);
    setPreviewState(data.body);
  };

  const handleClose = () => {
    setOpenConfirmCancel(false);
    setOpenConfirmUpdateState(false);
    setOpenSize(false);
  };

  const handleClickAway = (e: any) => {
    // TODO: Shet
    if (
      e.target.getAttribute('dir') !== 'rtl' &&
      e.target.getAttribute('type') !== 'checkbox' &&
      e.target.getAttribute('class') !== 'PrivateSwitchBase-input-73' &&
      e.target.getAttribute('class') !==
        'MuiTypography-root MuiListItemText-primary MuiTypography-body1 MuiTypography-displayBlock' &&
      e.target.getAttribute('aria-hidden') !== 'true'
    )
      setOpenFilterBox(false);
  };

  const findOrderStatus = (id: number) => {
    const status = props.ordersList.find(order => order.id === id)?.status;
    return status && translate(status, 'order');
  };

  const handleChangeSizePage = (event: React.ChangeEvent<{value: unknown}>) => {
    setFiltersValues({...filtersValues, page: 0, size: event.target.value});
  };

  return (
    <AuthConsumer>
      {({user}) => (
        <div className={classes.root}>
          <Grid
            style={{
              zIndex: 3,
              backgroundColor: 'white',
              position: 'fixed',
              top: '0',
              right: '71px',
            }}
            container
            direction="row"
            justify="space-between"
            alignItems="center"
          >
            <Paper
              elevation={1}
              style={{
                width: '100%',
                borderRadius: '0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div className={classes.headerBox}>
                <Typography variant="h6" component="h2">
                  {`لیست سفارش‌ها`}
                </Typography>
              </div>
              <div className={classes.searchBox}>
                <form
                  onSubmit={handleSubmit(onSubmit)}
                  className={classes.input}
                >
                  <IconButton
                    type="submit"
                    // className={classes.iconButton}
                    aria-label="search"
                  >
                    <Icon
                      path={mdiMagnify}
                      size={1}
                      className={classes.magnifyIcon}
                    />
                  </IconButton>
                  <InputBase
                    className={classes.input}
                    name="customerNationalCode.contains"
                    placeholder="کد ملی"
                    inputRef={register({required: true})}
                  />
                </form>

                <ClickAwayListener onClickAway={handleClickAway}>
                  <div>
                    <Button
                      type="button"
                      onClick={handleClick}
                      className={classes.iconButton}
                      aria-label="search"
                    >
                      {openFilterBox ? 'بستن' : 'فیلتر‌ها'}
                    </Button>
                    {openFilterBox ? (
                      <div
                        className={clsx(classes.filterBox, {
                          [classes.filterOpen]: openFilterBox,
                        })}
                      >
                        <RenderFilter
                          filters={filters}
                          onFilter={(filtervalues: any) => {
                            setFiltersValues({
                              ...removeEmptyValueObject(filtervalues),
                              'itemsSkuId.equals': props.match?.params?.id,
                              page: 0,
                              size: 10,
                            });
                            setOpenFilterBox(false);
                          }}
                          setOpenFilterBox={setOpenFilterBox}
                        />
                      </div>
                    ) : null}
                  </div>
                </ClickAwayListener>
              </div>
            </Paper>
          </Grid>

          <Card className={classes.card}>
            <CardHeader
              avatar={
                <Grid>
                  <Typography className={classes.typographyCards}>
                    لیست سفارش‌ها
                  </Typography>
                </Grid>
              }
              action={
                <div className={classes.btnBox}>
                  <Can
                    role={user.role}
                    perform="orders:btnGetNewOrders"
                    yes={() => (
                      <>
                        <Button
                          variant="contained"
                          color="secondary"
                          size={
                            checkMatchMedia('(max-width: 1024px)')
                              ? 'small'
                              : 'large'
                          }
                          onClick={() =>
                            props.actionRetrieveRubikaOrdersDispatch({})
                          }
                        >{`دریافت سفارش جدید`}</Button>
                      </>
                    )}
                  />

                  <Can
                    role={user.role}
                    perform="orders:downloadReportCSV"
                    yes={() => (
                      <>
                        <CSV
                          ordersDispatch={props.allOrdersDispatch}
                          data={props.allOrders}
                          filtersValues={filtersValues}
                          loading={props.loading}
                        />
                      </>
                    )}
                  />
                </div>
              }
            />

            <CardContent className={classes.cardContentOrder}>
              <Grid container lg={12} md={12} className={classes.cardContent}>
                <Grid container item lg={12} md={12}>
                  <Grid
                    container
                    item
                    xs={12}
                    md={12}
                    lg={12}
                    alignItems="center"
                  >
                    {props.loading ? (
                      <div className={classes.loading}>
                        <CircularProgress />
                      </div>
                    ) : (
                      <>
                        {props.ordersList.length > 0 ? (
                          <List
                            data={props.ordersList}
                            handleConfirmCanceleBox={handleOpenConfirmCancele}
                            changeStatusOrderWhitId={changeStatusOrderWhitId}
                            handleOpenConfirmUpdateState={
                              handleOpenConfirmUpdateState
                            }
                          />
                        ) : (
                          <Typography variant="h3" component="div">
                            سفارشی موجود نیست
                          </Typography>
                        )}
                      </>
                    )}
                  </Grid>
                  <Grid md={12} container style={{padding: '24px 16px 0 10px'}}>
                    <Grid item xs={6} sm={6}>
                      {props.ordersList.length > 0 && (
                        <FormControl
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                          }}
                        >
                          <div style={{marginLeft: '10px', color: '#8a8a8a'}}>
                            {' '}
                            تعداد نمایش در هر صفحه:
                          </div>
                          <Select
                            open={openSize}
                            onClose={handleClose}
                            onOpen={() => setOpenSize(true)}
                            value={filtersValues.size}
                            onChange={handleChangeSizePage}
                          >
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    </Grid>
                    <Grid xs={6} sm={6} container item direction="row-reverse">
                      {props.ordersList.length > 0 && (
                        <Pagination
                          className={classes.pagination}
                          count={Math.ceil(props.count / filtersValues.size)}
                          color="secondary"
                          page={filtersValues.page + 1}
                          onChange={handleChangePage}
                        />
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Dialog
            open={openConfirmCancel}
            fullWidth
            onClose={handleClose}
            aria-labelledby="responsive-dialog-title"
          >
            <DialogContent>
              <div
                style={{
                  position: 'absolute',
                  left: '17px',
                  zIndex: 10,
                }}
              >
                {handleClose ? (
                  <IconButton
                    aria-label="close"
                    // className={classes.closeButton}
                    onClick={handleClose}
                  >
                    <Icon path={mdiClose} size={1} />
                  </IconButton>
                ) : null}
              </div>
              <div className={classes.cancelIcon}>
                <Icon
                  path={mdiClose}
                  size={4}
                  color={theme.palette.error.main}
                />
              </div>
              <div
                className={classes.cancelTitle}
              >{`از لغو سفارش با شناسه ${id} مطمئن هستید؟`}</div>
              <TextField
                className={classes.textArea}
                placeholder={`علت لغو...`}
                value={cancelDescription}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setCancelDescription(e.target.value)
                }
              />
            </DialogContent>
            <DialogActions className={classes.actionsBox}>
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <Button
                    fullWidth
                    autoFocus
                    onClick={handleClose}
                    variant="contained"
                    color="secondary"
                    size="large"
                  >
                    {`خیر`}
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleCancelOrder}
                    disabled={!(cancelDescription.length > 0)}
                    size="large"
                  >
                    {`بله`}
                  </Button>
                </Grid>
              </Grid>
            </DialogActions>
          </Dialog>

          <Dialog
            fullWidth
            open={openConfirmUpdateState}
            onClose={handleClose}
            aria-labelledby="responsive-dialog-title"
          >
            <DialogContent>
              <div
                style={{
                  position: 'absolute',
                  left: '17px',
                  zIndex: 10,
                }}
              >
                {handleClose ? (
                  <IconButton
                    aria-label="close"
                    // className={classes.closeButton}
                    onClick={handleClose}
                  >
                    <Icon path={mdiClose} size={1} />
                  </IconButton>
                ) : null}
              </div>
              <div className={classes.editIcon}>
                <Icon
                  path={mdiPencilOutline}
                  size={4}
                  color={theme.palette.blue.main}
                />
              </div>
              <div className={classes.cancelTitle}>
                {`از تغییر تگ وضعیت سفارش
                با شناسه  ${id} از
 "${id > -1 && findOrderStatus(id)}"
 به
" ${translate(previewState.replace(/"/g, '') as Status, 'order')}"

 
 مطمئن هستید؟`}
              </div>
            </DialogContent>
            <DialogActions className={classes.actionsBox}>
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <Button
                    fullWidth
                    autoFocus
                    onClick={handleClose}
                    variant="contained"
                    color="secondary"
                    size="large"
                  >
                    {`خیر`}
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={changeStatusOrder}
                    size="large"
                  >
                    {`بله`}
                  </Button>
                </Grid>
              </Grid>
            </DialogActions>
          </Dialog>
        </div>
      )}
    </AuthConsumer>
  );
};

const mapStateToProps = createStructuredSelector<Store, SelectorProps>({
  ordersList: makeSelectOrdersList(),
  allOrders: makeSelectAllOrders(),
  count: makeSelectCountList(),
  loading: makeSelectLoading(),
  error: makeSelectError(),
});

function mapDispatchToProps(dispatch: Function) {
  return {
    ordersDispatch: (payload: Payload) => dispatch(getOrdersAction(payload)),
    allOrdersDispatch: (payload: Payload) =>
      dispatch(getAllOrdersReportAction(payload)),
    updateOrderStatusDispatch: (payload: {
      id: number;
      body: string;
      pagination: object;
    }) => dispatch(updateOrderStatusActions(payload)),
    updateAllOrderStatusDispatch: (payload: PayloadAllUpdate) =>
      dispatch(updateAllOrderStatusActions(payload)),
    actionRetrieveRubikaOrdersDispatch: (payload: {
      date?: String;
      from?: number;
      to?: number;
    }) => dispatch(actionRetrieveRubikaOrdersAction(payload)),
    cancelOrderDispatch: (payload: cancelPayload) =>
      dispatch(cancelOrderAction(payload)),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);
export default compose(withConnect)(VariantOrders);
